import { useEffect, useMemo, useState, useCallback } from "react";
import {
  Alert,
  Button,
  Card,
  Input,
  Select,
  Spin,
  Tooltip,
  Typography,
  message,
  Modal,
  Result,
} from "antd";
import {
  ReloadOutlined,
  BankOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  SearchOutlined,
  DownOutlined,
  UpOutlined,
  RightOutlined,
} from "@ant-design/icons";
import {
  getInstitutionManageAll,
  getInstitutionHead,
  activateInstitution,
  deactivateInstitution,
} from "../api/institutionApi";
import { syncBankBalances } from "../api/bankAccountApi";
import { hasAnyRole } from "../api/auth";
import TradingHouseList from "../components/institutions/TradingHouseList";
import { useOpenRoute } from "../utils/openRouteUtils";

const { Title, Text } = Typography;

const HEAD_INSTITUTION_ID = 14;
const FILTER_STORAGE_KEY = "head-institution-control:trading-house-filter";
const CURRENCY_ORDER = ["MNT", "USD", "EUR", "CNY", "KRW", "JPY"];

function formatAmount(value) {
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value ?? 0));
}

function sortByCurrencyOrder(arr) {
  return [...arr].sort((a, b) => {
    const ai = CURRENCY_ORDER.indexOf(a.currencyCode);
    const bi = CURRENCY_ORDER.indexOf(b.currencyCode);
    if (ai === -1 && bi === -1) return a.currencyCode.localeCompare(b.currencyCode);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}

export default function HeadInstitutionControllPage() {
  const openRoute = useOpenRoute();
  const isAdmin = hasAnyRole(["ADMIN"]);
  const isModerator = hasAnyRole(["MODERATOR"]);
  const isAuthor = hasAnyRole(["AUTHOR"]);

  const canViewPage = isAdmin || isModerator || isAuthor;
  const canManage = isAdmin || isModerator;
  const canSeeHeadInstitution = isAdmin;

  const [data, setData] = useState([]);
  const [headInstitution, setHeadInstitution] = useState(null);
  const [loading, setLoading] = useState(canViewPage);
  const [error, setError] = useState("");
  const [togglingInstitutionId, setTogglingInstitutionId] = useState(null);
  const [syncingBalances, setSyncingBalances] = useState(false);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);
  const [statsExpanded, setStatsExpanded] = useState(false);

  const [verifyModal, setVerifyModal] = useState({
    open: false,
    action: null,
    item: null,
    input: "",
  });

  const [searchText, setSearchText] = useState(() => {
    return localStorage.getItem(`${FILTER_STORAGE_KEY}:search`) || "";
  });

  const [selectedInstitutionIds, setSelectedInstitutionIds] = useState(() => {
    try {
      const stored = localStorage.getItem(`${FILTER_STORAGE_KEY}:institutionIds`);
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });

  const closeVerifyModal = () => {
    setVerifyModal({
      open: false,
      action: null,
      item: null,
      input: "",
    });
  };

  const loadData = useCallback(async () => {
    if (!canViewPage) return;

    try {
      setLoading(true);
      setError("");

      const [allResponse, headResponse] = await Promise.all([
        getInstitutionManageAll(),
        canSeeHeadInstitution ? getInstitutionHead() : Promise.resolve(null),
      ]);
      setData(Array.isArray(allResponse) ? allResponse : []);
      setHeadInstitution(headResponse || null);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Байгууллагын мэдээллийг ачаалж чадсангүй");
      setData([]);
      setHeadInstitution(null);
    } finally {
      setLoading(false);
    }
  }, [canViewPage, canSeeHeadInstitution]);

  useEffect(() => {
    if (canViewPage) {
      loadData();
    }
  }, [canViewPage, loadData]);

  useEffect(() => {
    localStorage.setItem(`${FILTER_STORAGE_KEY}:search`, searchText);
  }, [searchText]);

  useEffect(() => {
    localStorage.setItem(
      `${FILTER_STORAGE_KEY}:institutionIds`,
      JSON.stringify(selectedInstitutionIds)
    );
  }, [selectedInstitutionIds]);

  const visibleInstitutions = useMemo(() => {
    return data.filter((item) => item?.institution?.id !== HEAD_INSTITUTION_ID);
  }, [data]);

  const allTradingHouses = useMemo(() => {
    return visibleInstitutions.filter(
      (item) => item?.institution?.type === "TRADING_HOUSE"
    );
  }, [visibleInstitutions]);

  const tradingHouseOptions = useMemo(() => {
    return allTradingHouses
      .map((item) => ({
        value: String(item?.institution?.id),
        label:
          item?.institution?.name || `Institution ${item?.institution?.id ?? ""}`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [allTradingHouses]);

  const filteredTradingHouses = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return allTradingHouses.filter((item) => {
      const institutionId = String(item?.institution?.id ?? "");
      const institutionName = item?.institution?.name?.toLowerCase() || "";

      const matchesSelect =
        selectedInstitutionIds.length === 0 ||
        selectedInstitutionIds.includes(institutionId);

      const matchesSearch =
        !normalizedSearch ||
        institutionName.includes(normalizedSearch) ||
        institutionId.includes(normalizedSearch);

      return matchesSelect && matchesSearch;
    });
  }, [allTradingHouses, searchText, selectedInstitutionIds]);

  const activeTradingHouses = useMemo(() => {
    return filteredTradingHouses.filter((item) =>
      Boolean(item?.institution?.is_active)
    );
  }, [filteredTradingHouses]);

  const hiddenTradingHouses = useMemo(() => {
    return filteredTradingHouses.filter(
      (item) => !Boolean(item?.institution?.is_active)
    );
  }, [filteredTradingHouses]);

  const aggregatedStats = useMemo(() => {
    const map = {};
    for (const item of allTradingHouses) {
      for (const cur of (item?.currencies || [])) {
        const code = cur?.currencyCode;
        if (!code) continue;
        if (!map[code]) map[code] = { currencyCode: code, totalWallet: 0, totalBalance: 0, accountCount: 0 };
        map[code].totalWallet += Number(cur?.totalWallet ?? 0);
        map[code].totalBalance += Number(cur?.totalBalance ?? 0);
        map[code].accountCount += Number(cur?.accountCount ?? 0);
      }
    }
    return sortByCurrencyOrder(Object.values(map));
  }, [allTradingHouses]);

  const totalActiveCount = useMemo(
    () => allTradingHouses.filter((item) => Boolean(item?.institution?.is_active)).length,
    [allTradingHouses]
  );

  const totalInactiveCount = useMemo(
    () => allTradingHouses.filter((item) => !Boolean(item?.institution?.is_active)).length,
    [allTradingHouses]
  );

  const handleOpenAccounts = (item, event) => {
    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/accounts`, event);
  };

  const handleOpenHeadInstitutionAccounts = (item, event) => {
    if (!canSeeHeadInstitution) return;

    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/head-accounts`, event);
  };

  const handleOpenWallets = (item, event) => {
    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    openRoute(`/institutions/${institutionId}/wallets`, event);
  };

  const handleResetFilters = () => {
    setSearchText("");
    setSelectedInstitutionIds([]);
  };

  const handleTempSyncBalances = async () => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    try {
      setSyncingBalances(true);
      await syncBankBalances();
      message.success("Банкны дансны үлдэгдэл амжилттай шинэчлэгдлээ");
    } catch (err) {
      console.error(err);
      message.error("Банкны дансны үлдэгдлийг шинэчлэхэд алдаа гарлаа");
    } finally {
      setSyncingBalances(false);
    }
  };

  const handleToggleInstitution = (item, checked) => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    const institutionId = item?.institution?.id;
    if (!institutionId) return;

    setVerifyModal({
      open: true,
      action: checked ? "activate" : "deactivate",
      item,
      input: "",
    });
  };

  const handleConfirmToggle = async () => {
    if (!canManage) {
      message.error("Танд энэ үйлдлийг хийх эрх алга");
      return;
    }

    const { item, action, input } = verifyModal;
    const institutionId = item?.institution?.id;

    if (!institutionId || !action) return;

    const expectedText = item?.institution?.name?.trim() || "";
    const normalizedInput = String(input || "").trim();

    if (normalizedInput !== expectedText) {
      message.error(`"${expectedText}" гэж бичнэ үү`);
      return;
    }

    try {
      setTogglingInstitutionId(institutionId);

      if (action === "activate") {
        await activateInstitution(institutionId);
        message.success("Байгууллага амжилттай идэвхжлээ");
      } else {
        await deactivateInstitution(institutionId);
        message.success("Байгууллага идэвхгүй боллоо");
      }

      await loadData();
      closeVerifyModal();
    } catch (err) {
      console.error(err);
      message.error(err?.message || "Байгууллагын төлөв шинэчлэхэд алдаа гарлаа");
    } finally {
      setTogglingInstitutionId(null);
    }
  };

  if (!canViewPage) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-8">
        <div className="mx-auto max-w-4xl">
          <Card className="rounded-2xl border-0 shadow-sm">
            <Result
              status="403"
              title="403"
              subTitle="Энэ хуудсанд хандах эрх алга"
            />
          </Card>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex min-h-[320px] items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  const isFiltered = searchText.trim() !== "" || selectedInstitutionIds.length > 0;

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">

        {/* Page header */}
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <Title level={4} className="!mb-0 text-slate-900">
              Байгууллагын удирдлага
            </Title>
            <Text className="text-sm text-slate-500">
              Трейдинг хаусуудын жагсаалт ба удирдлага
            </Text>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {canManage ? (
              <Button
                type="primary"
                loading={syncingBalances}
                onClick={handleTempSyncBalances}
              >
                Үлдэгдэл синк хийх
              </Button>
            ) : null}
            <Button icon={<ReloadOutlined />} onClick={loadData}>
              Шинэчлэх
            </Button>
          </div>
        </div>

        {error ? (
          <Card className="mb-6 rounded-2xl">
            <Alert
              type="error"
              message="Ачааллахад алдаа гарлаа"
              description={error}
              showIcon
            />
          </Card>
        ) : null}

        {canSeeHeadInstitution && headInstitution ? (
          <Card
            className="mb-6 rounded-[28px] border-0 bg-white shadow-sm"
            styles={{ body: { padding: 0 } }}
          >
            {/* Institution header */}
            <div className="flex flex-col gap-5 px-6 py-5 md:flex-row md:items-center md:justify-between md:px-7">
              <div className="flex min-w-0 items-center gap-4">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-700">
                  <BankOutlined className="text-[22px]" />
                </div>
                <div className="min-w-0">
                  <div className="mb-1 text-[15px] font-medium uppercase tracking-[0.28em] text-slate-500">
                    Банк бус
                  </div>
                  <div className="truncate text-[28px] font-semibold leading-tight text-slate-900">
                    {headInstitution?.institution?.name ||
                      `Institution ${HEAD_INSTITUTION_ID}`}
                  </div>
                  <span className="text-md text-slate-500">
                    ID {HEAD_INSTITUTION_ID ?? "-"}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  icon={<DatabaseOutlined />}
                  className="h-10 rounded-xl border-slate-200 px-4"
                  onClick={(e) =>
                    handleOpenHeadInstitutionAccounts(headInstitution, e)
                  }
                >
                  Данснууд
                </Button>
              </div>
            </div>

            {/* Collapsible stats footer */}
            <div className="border-t border-slate-100">
              <button
                type="button"
                onClick={() => setStatsExpanded((prev) => !prev)}
                className="flex w-full items-center justify-between px-6 py-3.5 text-left transition-colors hover:bg-slate-50 md:px-7"
              >
                <div className="flex items-center gap-2.5">
                  <BarChartOutlined className="text-sm text-slate-400" />
                  <span className="text-sm font-medium text-slate-600">
                    Бүх трединг хаусyудын нийт валютын үлдэгдэл
                  </span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-xs text-slate-400">
                    {aggregatedStats.length} валют · {allTradingHouses.length} байгууллага
                  </span>
                  {statsExpanded ? (
                    <UpOutlined className="text-[10px] text-slate-400" />
                  ) : (
                    <DownOutlined className="text-[10px] text-slate-400" />
                  )}
                </div>
              </button>

              {statsExpanded ? (
                <div className="px-6 pb-6 md:px-7">
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {aggregatedStats.length === 0 ? (
                      <div className="col-span-full py-4 text-center text-sm text-slate-400">
                        Мэдээлэл байхгүй
                      </div>
                    ) : (
                      aggregatedStats.map((stat) => {
                        const wallet = Number(stat.totalWallet);
                        const balance = Number(stat.totalBalance);
                        const walletClass =
                          wallet < 0
                            ? "text-red-600"
                            : wallet > 0
                            ? "text-slate-900"
                            : "text-slate-600";
                        return (
                          <div
                            key={stat.currencyCode}
                            className="overflow-hidden rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4"
                          >
                            <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
                              {stat.currencyCode}
                            </div>
                            <Tooltip title="Хийсвэр үлдэгдэл">
                              <div className={`mt-2 break-all text-[15px] font-semibold leading-snug ${walletClass}`}>
                                {formatAmount(wallet)}
                              </div>
                            </Tooltip>
                            <Tooltip title="Жинхэнэ үлдэгдэл">
                              <div className="mt-1 cursor-default break-all text-[13px] text-slate-500">
                                {formatAmount(balance)}
                              </div>
                            </Tooltip>
                            <div className="mt-2 border-t border-slate-200 pt-2 text-xs text-slate-400">
                              {stat.accountCount} данс
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </Card>
        ) : null}

        {/* Filters */}
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center">
          <Input
            allowClear
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Трейдинг хаус хайх"
            prefix={<SearchOutlined className="text-slate-400" />}
            className="h-11 min-w-[260px] rounded-xl"
          />
          <Select
            mode="multiple"
            value={selectedInstitutionIds}
            onChange={setSelectedInstitutionIds}
            className="min-w-[260px]"
            size="large"
            options={tradingHouseOptions}
            showSearch
            optionFilterProp="label"
            placeholder="Трейдинг хаус сонгох"
            maxTagCount="responsive"
            allowClear
          />
          <Button
            className="h-11 rounded-xl border-slate-200 px-4"
            onClick={handleResetFilters}
            disabled={!isFiltered}
          >
            Цэвэрлэх
          </Button>
        </div>

        {/* Summary stats */}
        <div className="mb-5 flex flex-wrap gap-3">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
            <Text className="text-sm text-slate-500">Нийт</Text>
            <Text strong>{filteredTradingHouses.length}</Text>
            {isFiltered ? (
              <Text className="text-xs text-slate-400">/ {allTradingHouses.length}</Text>
            ) : null}
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
            <span className="h-2 w-2 rounded-full bg-green-500" />
            <Text className="text-sm text-slate-500">Идэвхтэй</Text>
            <Text strong className="text-green-600">{activeTradingHouses.length}</Text>
            {isFiltered ? (
              <Text className="text-xs text-slate-400">/ {totalActiveCount}</Text>
            ) : null}
          </div>
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
            <span className="h-2 w-2 rounded-full bg-slate-400" />
            <Text className="text-sm text-slate-500">Идэвхгүй</Text>
            <Text strong className="text-slate-500">{hiddenTradingHouses.length}</Text>
            {isFiltered ? (
              <Text className="text-xs text-slate-400">/ {totalInactiveCount}</Text>
            ) : null}
          </div>
        </div>

        <div className="space-y-10">
          {/* Active section */}
          <div>
            <div className="mb-3 flex items-center justify-between">
              <Title level={5} className="!mb-0">
                Идэвхтэй трейдинг хаусууд
              </Title>
              <Text className="text-slate-500">{activeTradingHouses.length}</Text>
            </div>
            <TradingHouseList
              items={activeTradingHouses}
              onAccounts={handleOpenAccounts}
              onWallets={handleOpenWallets}
              onToggleActive={handleToggleInstitution}
              togglingInstitutionId={togglingInstitutionId}
              canManage={canManage}
              emptyText="Идэвхтэй трейдинг хаус олдсонгүй"
            />
          </div>

          {/* Inactive section — collapsed by default */}
          {hiddenTradingHouses.length > 0 ? (
            <div>
              <button
                type="button"
                className="mb-4 flex w-full items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 text-left shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                onClick={() => setInactiveExpanded((prev) => !prev)}
              >
                <div className="flex items-center gap-3">
                  <span className="h-2.5 w-2.5 rounded-full bg-slate-400" />
                  <Title level={5} className="!mb-0 text-slate-600">
                    Идэвхгүй трейдинг хаусууд
                  </Title>
                </div>
                <div className="flex items-center gap-3">
                  <span className="rounded-full bg-slate-100 px-3 py-0.5 text-sm font-semibold text-slate-600">
                    {hiddenTradingHouses.length}
                  </span>
                  {inactiveExpanded ? (
                    <DownOutlined className="text-slate-500" />
                  ) : (
                    <RightOutlined className="text-slate-500" />
                  )}
                </div>
              </button>
              {inactiveExpanded ? (
                <TradingHouseList
                  items={hiddenTradingHouses}
                  onAccounts={handleOpenAccounts}
                  onWallets={handleOpenWallets}
                  onToggleActive={handleToggleInstitution}
                  togglingInstitutionId={togglingInstitutionId}
                  canManage={canManage}
                  emptyText="Идэвхгүй трейдинг хаус олдсонгүй"
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      <Modal
        open={verifyModal.open}
        onCancel={closeVerifyModal}
        onOk={handleConfirmToggle}
        okButtonProps={{
          disabled:
            String(verifyModal.input || "").trim() !==
            (verifyModal.item?.institution?.name?.trim() || ""),
          loading: togglingInstitutionId != null,
          danger: verifyModal.action === "deactivate",
        }}
        cancelButtonProps={{
          disabled: togglingInstitutionId != null,
        }}
        title={
          verifyModal.action === "activate"
            ? "Идэвхжүүлэхийг баталгаажуулах"
            : "Идэвхгүй болгохыг баталгаажуулах"
        }
      >
        <div className="flex flex-col gap-3">
          <div className="text-sm text-slate-600">
            {verifyModal.action === "activate"
              ? "Энэ үйлдэл байгууллагыг идэвхжүүлнэ."
              : "Энэ үйлдэл байгууллагыг идэвхгүй болгоно. Энэ нь гүйлгээ болон тайланд нөлөөлж болзошгүй."}
          </div>
          <div className="text-sm text-slate-700">
            Баталгаажуулахын тулд байгууллагын нэрийг бичнэ үү:{" "}
            <span className="font-semibold text-slate-900">
              {verifyModal.item?.institution?.name}
            </span>
          </div>
          <Input
            value={verifyModal.input}
            onChange={(e) =>
              setVerifyModal((prev) => ({ ...prev, input: e.target.value }))
            }
            placeholder={verifyModal.item?.institution?.name || ""}
            onPressEnter={handleConfirmToggle}
            disabled={togglingInstitutionId != null}
          />
        </div>
      </Modal>
    </div>
  );
}